import { AppDataSource } from "global/DataSources/AppDataSource";
import { IAccountsReceivable, IBenefit, IEntityReferenceValue, IFeesAndPremiums, IFlexibleField, IFlexibleFieldSet, IGroupConfigurationData, IGroupNumber, INonStandard, IPlan, IStopLoss } from "model/FlexibleAttributeModel";
import { BaseJsonTransformer, ITransformContext } from "./IJsonTransformer";

interface FieldUpdateRequest {
    field: IFlexibleField;
    value: IEntityReferenceValue;
    logicalName: string;
    id: string;
}

/**
 * Design assumptions:
 * - There are over 700 fields that are of type Lookup, Costshare, or Optionset
 * - Most of those are Costshare or OptionSet
 * - There are over 1300 OptionSet values
 * - There are over 700 Costshare values
 * - The Lookup type includes accounts/contacts
 */
export class UpdateLookupValueNameTransformer extends BaseJsonTransformer {
    readonly dataSource: AppDataSource;
    readonly fieldRequests = new Map<string, FieldUpdateRequest[]>();

    constructor(dataSource: AppDataSource) {
        super();

        this.dataSource = dataSource;
    }

    async before(json: IGroupConfigurationData): Promise<void> {
        this.fieldRequests.clear();
    }

    async transformGroupConfiguration(json: IGroupConfigurationData): Promise<void> {
        this.processFields(json.fields);
    }

    async transformAttributeSet(attributeSet: IFlexibleFieldSet, context: ITransformContext): Promise<boolean | void> {
        this.processFields(attributeSet.fields);
    }

    async transformNonStandard(nonStandard: INonStandard, context: ITransformContext): Promise<boolean | void> {
        this.processFields(nonStandard.fields);
    }

    async transformBenefit(benefit: IBenefit): Promise<boolean | void> {
        this.processFields(benefit.fields);
    }

    async transformAccountsReceivable(accountsReceivable: IAccountsReceivable): Promise<boolean | void> {
        this.processFields(accountsReceivable.fields);
    }

    async transformStopLoss(stopLoss: IStopLoss): Promise<boolean | void> {
        this.processFields(stopLoss.fields);
    }

    async transformFeesAndPremium(feesAndPremium: IFeesAndPremiums): Promise<boolean | void> {
        this.processFields(feesAndPremium.fields);
    }

    async transformPlan(plan: IPlan): Promise<boolean | void> {
        this.processFields(plan.fields);
    }

    async transformGroupNumber(groupNumber: IGroupNumber): Promise<boolean | void> {
        this.processFields(groupNumber.fields);
    }

    async after(json: IGroupConfigurationData): Promise<void> {
        const batchSize = 500;

        // get updated names by getting the unique set of IDs and querying for those
        for (const [logicalName, fieldRequests] of this.fieldRequests) {
            const data = new Map<string, string>();

            // get the unique set of GUIDs
            const uniqueIds = [...new Set(fieldRequests.map(r => r.id))];

            // get the entity metadata
            const metadata = await this.dataSource.crm.getEntityMetadata(logicalName, '?$select=LogicalCollectionName,PrimaryNameAttribute,PrimaryIdAttribute');

            // build a query for the unique ids, batching by 500 (there's a SQL limitation to
            // the number of conditions a clause can have)
            while (uniqueIds.length > 0) {
                const batch = uniqueIds.splice(0, batchSize);
                const query = `${metadata.LogicalCollectionName}?$select=${metadata.PrimaryNameAttribute},${metadata.PrimaryIdAttribute}&$filter=${batch.map(id => `${metadata.PrimaryIdAttribute} eq ${id}`).join(` or `)}`;
                const results = await this.dataSource.crm.queryBatch(query);
                results.forEach(element => {
                    data.set(element[metadata.PrimaryIdAttribute], element[metadata.PrimaryNameAttribute]);
                });
            }

            // now match up
            for (const request of fieldRequests) {
                const newValue = data.get(request.id);
                if (newValue) {
                    request.value.name = newValue;
                }
            }
        }
    }

    private processFields(fields: IFlexibleField[]): void {
        for (let field of fields) {
            if (!field || !field.values)
                continue;

            for (let value of field.values) {
                if (!value || !value.value)
                    continue;

                if (value.value.type === "entity") {
                    let entityType = value.value.entityType;

                    if (entityType === 'bcbs_optionsetitem') {
                        entityType = 'bcbs_config_optionsetitem';
                    }

                    if (entityType === 'costshare') {
                        entityType = 'bcbs_copaycoinoption';
                    }


                    if (entityType === 'bcbs_admin') {
                        entityType = 'bcbs_bccadmin';
                    }

                    let logicalNameRequests = this.fieldRequests.get(entityType);

                    if (!logicalNameRequests) {
                        logicalNameRequests = [];
                        this.fieldRequests.set(entityType, logicalNameRequests);
                    }

                    logicalNameRequests.push({
                        field: field,
                        value: value.value,
                        id: value.value.id,
                        logicalName: entityType
                    });
                }
            }
        }
    }
}